import { Box, Button, Checkbox, Divider, Group, Skeleton, Stack, Text } from '@mantine/core';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { Right } from 'components/Right';
import { tKeys, useTranslate } from 'locales/index';
import type { ActionType, UnhandledInvoice } from 'modules/AtRisk/api/use-load-unhandled-client';
import { useId, useState } from 'react';
import { prop } from 'remeda';
import type { AdditionalResourceInvoice } from './api/use-load-additional-resources';
import { useLoadAdditionalResources } from './api/use-load-additional-resources';
import { InvoiceCard } from './InvoiceCard';

export type AdditionalResourcesSelection = {
  invoiceIds: Array<string>;
};

type ChooseAdditionalInvoicesProps = {
  selectedInvoiceIds: Array<string>;
  unhandledInvoices: Array<UnhandledInvoice>;
  actionType: ActionType | 'pause';
  onNext: (selection: AdditionalResourcesSelection) => void;
  // When false, the additional invoices are not fetched or shown — only the unhandled
  // invoices section is rendered.
  showAdditionalResources?: boolean;
  // When true, every unhandled invoice is selectable regardless of its
  // availableActions — used by actions the engine never marks per-invoice (pause).
  areAllInvoicesEligible?: boolean;
  // True when submitting performs the action straight away rather than opening a
  // further step, which is what the button label announces.
  isFinalStep?: boolean;
};

export const ChooseAdditionalInvoices = ({
  selectedInvoiceIds,
  unhandledInvoices,
  actionType,
  onNext,
  showAdditionalResources = true,
  areAllInvoicesEligible = false,
  isFinalStep = false,
}: ChooseAdditionalInvoicesProps) => {
  const t = useTranslate();
  const { id: debtorId } = useDebtor();

  const compatibleInvoices = areAllInvoicesEligible
    ? unhandledInvoices
    : unhandledInvoices.filter(invoice =>
        invoice.availableActions?.some(action => action === actionType),
      );
  const compatibleIds = new Set(compatibleInvoices.map(prop('id')));

  const [selectedUnhandledIds, setSelectedUnhandledIds] = useState<Array<string>>(
    selectedInvoiceIds.filter(id => compatibleIds.has(id)),
  );
  const [selectedAdditionalInvoiceIds, setSelectedAdditionalInvoiceIds] = useState<Array<string>>(
    [],
  );

  const { additionalResources, isAdditionalResourcesInitialLoading } = useLoadAdditionalResources(
    { debtorId: Number(debtorId), actionType },
    // Skip the network call entirely when extra resources are hidden; the empty
    // placeholder then makes only the unhandled invoices section render.
    { enabled: showAdditionalResources },
  );

  const toggle = (setState: React.Dispatch<React.SetStateAction<Array<string>>>, id: string) =>
    setState(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const handleNext = () => {
    onNext({ invoiceIds: [...selectedUnhandledIds, ...selectedAdditionalInvoiceIds] });
  };

  const hasSelection = selectedUnhandledIds.length + selectedAdditionalInvoiceIds.length > 0;

  if (isAdditionalResourcesInitialLoading) {
    return <InvoiceSelectionSkeleton rowCount={compatibleInvoices.length} />;
  }

  // The API's scope overlaps the unhandled section (e.g. dunning actions return
  // every late invoice); rows already rendered above must not appear twice.
  const additionalInvoices = additionalResources.invoices.filter(
    invoice => !compatibleIds.has(String(invoice.id)),
  );

  return (
    <Stack gap="md" miw={400}>
      {/* Unhandled invoices — all pre-selected, deselectable */}
      <SelectableSection title={t(tKeys.UNHANDLED_INVOICES)}>
        {compatibleInvoices.map((invoice, i) => (
          <SelectableRow
            key={invoice.id}
            checked={selectedUnhandledIds.includes(invoice.id)}
            onChange={() => toggle(setSelectedUnhandledIds, invoice.id)}
            isLast={i === compatibleInvoices.length - 1}
          >
            <InvoiceCard invoice={invoice} hideBorder />
          </SelectableRow>
        ))}
      </SelectableSection>

      {/* Additional invoices from the API — none pre-selected */}
      {additionalInvoices.length > 0 && (
        <>
          <Divider />
          <SelectableSection title={t(tKeys.ADDITIONAL_INVOICES)}>
            {additionalInvoices.map((invoice, i) => (
              <SelectableRow
                key={invoice.id}
                checked={selectedAdditionalInvoiceIds.includes(String(invoice.id))}
                onChange={() => toggle(setSelectedAdditionalInvoiceIds, String(invoice.id))}
                isLast={i === additionalInvoices.length - 1}
              >
                <InvoiceCard invoice={toUnhandledInvoice(invoice)} hideBorder />
              </SelectableRow>
            ))}
          </SelectableSection>
        </>
      )}

      <Divider />
      <Right>
        <Button size="xs" onClick={handleNext} disabled={!hasSelection}>
          {t(isFinalStep ? tKeys.CONFIRM : tKeys.NEXT)}
        </Button>
      </Right>
    </Stack>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

type SelectableSectionProps = { title: string; children: React.ReactNode };

const SelectableSection = ({ title, children }: SelectableSectionProps) => {
  const titleId = useId();

  return (
    <Box role="group" aria-labelledby={titleId}>
      <Text id={titleId} size="sm" fw="bold" mb="xs">
        {title}
      </Text>
      <Box bdrs={8} bd="1px solid var(--mantine-color-gray-2)">
        {children}
      </Box>
    </Box>
  );
};

type SelectableRowProps = {
  checked: boolean;
  onChange: () => void;
  isLast: boolean;
  children: React.ReactNode;
};

const SelectableRow = ({ checked, onChange, isLast, children }: SelectableRowProps) => (
  <Group
    component="label"
    gap="xs"
    pl={8}
    align="center"
    style={{
      cursor: 'pointer',
      borderBottom: isLast ? undefined : '1px solid var(--mantine-color-gray-2)',
    }}
  >
    <Checkbox size="xs" checked={checked} onChange={onChange} />
    <Box style={{ flex: 1 }}>{children}</Box>
  </Group>
);

// ─── Skeleton ─────────────────────────────────────────────────────────────────

type InvoiceSelectionSkeletonProps = { rowCount: number };

const InvoiceSelectionSkeleton = ({ rowCount }: InvoiceSelectionSkeletonProps) => (
  <Stack gap="md" miw={400}>
    <Box>
      <Skeleton height={16} width={120} mb="xs" />
      <Box bdrs={8} bd="1px solid var(--mantine-color-gray-2)">
        {Array.from({ length: rowCount }, (_, index) => index).map(index => (
          <Group
            key={`invoice-selection-skeleton-${index}`}
            gap="xs"
            pl={8}
            align="center"
            style={{
              borderBottom:
                index < rowCount - 1 ? '1px solid var(--mantine-color-gray-2)' : undefined,
            }}
          >
            <Skeleton height={12} width={12} />
            <Group py={6} px={8} style={{ flex: 1 }} justify="space-between">
              <Group gap="xs">
                <Skeleton height={10} width={100} />
                <Skeleton height={10} width={60} />
              </Group>
              <Skeleton height={10} width={60} />
            </Group>
          </Group>
        ))}
      </Box>
    </Box>
  </Stack>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toUnhandledInvoice(invoice: AdditionalResourceInvoice): UnhandledInvoice {
  return {
    id: String(invoice.id),
    reference: invoice.reference,
    totalTvac: invoice.localizedMoneyObject.totalTvac,
    dueDate: invoice.issueDate,
  };
}
